import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { map } from 'rxjs';
import { Shot } from 'src/app/components/company/models/new/shot';
import { OrderMapper } from 'src/app/components/company/services/order-mapper';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { DatabaseQueries } from 'src/app/shared/types/get-query';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';

type MediaKind = 'image' | 'video' | 'unsupported';

@Component({
  selector: 'bt-media-history-modal',
  standalone: false,
  templateUrl: './media-history-modal.component.html',
  styleUrl: './media-history-modal.component.scss'
})
export class MediaHistoryModalComponent {
  readonly #dialogRef = inject(MatDialogRef<MediaHistoryModalComponent>);
  readonly #data = inject<{ shotId: number }>(MAT_DIALOG_DATA);
  readonly #destroyRef = inject(DestroyRef);
  readonly #graphQlService = inject(GraphQLService);
  readonly #orderMapper = inject(OrderMapper);
  readonly #utilitiesService = inject(UtilitiesService);
  readonly #router = inject(Router);

  loading = true;
  saving = false;
  shots: Shot[] = [];
  currentIndex = 0;
  mediaKind: MediaKind = 'unsupported';

  constructor() {
    this.loadImageHistory();
  }

  get currentShot(): Shot | undefined {
    return this.shots[this.currentIndex];
  }

  loadImageHistory(): void {
    if (!this.#data?.shotId) {
      this.loading = false;
      return;
    }

    this.loading = true;

    this.#graphQlService
      .getData<any>(DatabaseQueries.GET_IMAGE_HISTORY, { imageId: this.#data.shotId })
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        map((res: any) => (res?.data?.imageHistory?.nodes || []).map((node: any) => this.#orderMapper.toShot(node)))
      )
      .subscribe({
        next: (shots: Shot[]) => {
          this.shots = shots.reverse();
          const selectedIndex = shots.findIndex((shot) => shot.selected);
          this.currentIndex = selectedIndex >= 0 ? selectedIndex : 0;
          this.updateMediaKind();
          this.loading = false;
        },
        error: (err) => {
          console.error(err);
          this.#utilitiesService.showSnackbar('Error loading media history', SnackbarType.Error);
          this.loading = false;
        }
      });
  }

  selectShot(index: number): void {
    if (index < 0 || index >= this.shots.length) return;

    this.currentIndex = index;
    this.updateMediaKind();
  }

  goPrevious(): void {
    if (this.isFirst()) return;

    this.currentIndex -= 1;
    this.updateMediaKind();
  }

  goNext(): void {
    if (this.isLast()) return;

    this.currentIndex += 1;
    this.updateMediaKind();
  }

  isFirst(): boolean {
    return this.currentIndex <= 0;
  }

  isLast(): boolean {
    return this.currentIndex >= this.shots.length - 1;
  }

  close(): void {
    this.#dialogRef.close(false);
  }

  openSourceOrder(orderId?: number): void {
    if (!orderId) return;

    this.#dialogRef.close(false);
    this.#router.navigate(['/company/order-view/' + orderId + '/order-detail-info']);
  }

  selectThisImage(): void {
    const imageId = this.currentShot?.id;
    if (!imageId || this.saving) return;

    this.saving = true;

    this.#graphQlService
      .postData(MutationQuery.SET_IMAGE_AS_SELECTED, {
        setImageAsSelectedInput: { imageId }
      })
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (res: any) => {
          const result = res?.data?.setImageAsSelected;
          this.saving = false;

          if (result?.success) {
            this.#utilitiesService.showSnackbar(result.message || 'Image selected successfully', SnackbarType.Success);
            this.#dialogRef.close(true);
            return;
          }

          this.#utilitiesService.showSnackbar(result?.message || 'Error selecting image', SnackbarType.Error);
        },
        error: (err) => {
          console.error(err);
          this.saving = false;
          this.#utilitiesService.showSnackbar('Error selecting image', SnackbarType.Error);
        }
      });
  }

  getShotMediaKind(shot?: Shot): MediaKind {
    return this.resolveMediaKind(shot?.imgPath || '');
  }

  getExtension(src: string): string {
    if (!src) {
      return '';
    }

    const pathWithoutQuery = src.split('?')[0];
    const lastSegment = pathWithoutQuery.split('/').pop() ?? '';
    const dotIndex = lastSegment.lastIndexOf('.');

    if (dotIndex < 0) {
      return '';
    }

    return lastSegment.substring(dotIndex + 1).toLowerCase();
  }

  private updateMediaKind(): void {
    this.mediaKind = this.resolveMediaKind(this.currentShot?.imgPath || '');
  }

  private resolveMediaKind(src: string): MediaKind {
    const extension = this.getExtension(src);

    if (['jpeg', 'jpg', 'png'].includes(extension)) {
      return 'image';
    }

    if (['mkv', 'mp4'].includes(extension)) {
      return 'video';
    }

    return 'unsupported';
  }
}
